import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  Tooltip
} from '@mui/material'
import { TextField } from 'components/textField'
import { inject, observer } from 'mobx-react'
import { User } from 'models'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AuthStore } from 'stores'
import { Language, UserType } from 'types'
import { emailValidation } from 'util/util'

export interface IUserFormProps {
  authStore?:              AuthStore,
  hasPersonalNumber?:      boolean,
  user?:                   User,
  isSystemUser?:           boolean,
  selectRole?:             boolean,
  dontSave?:               boolean,
  required?:               boolean,
  hasConfirmationButtons?: boolean,
  onSave?:   () => void,
  onCancel?: () => void,
}

export const UserForm = inject('authStore', 'routingStore')(observer((props: IUserFormProps) => {
  const { t } = useTranslation();
  const {
    authStore,
    hasPersonalNumber,
    hasConfirmationButtons,
    user,
    isSystemUser,
    selectRole,
    dontSave,
    required,
    onSave,
    onCancel,
  } = props;

  const [touched, setTouched] = React.useState<{
    first?: true,
    last?:  true,
    pn?:    true,
    mail?:  true,
  }>({});

  const _user: User     = user || authStore.getCurUser;

  const valid = [
    !!_user.email,
    !!_user.first_name,
    !!_user.last_name,
    hasPersonalNumber ? Boolean(_user.personal_number && _user.personal_number.length === 12) : true,
    isSystemUser ? !_user.user_type : true,
    !!emailValidation(_user.email)
  ];
  
  const disableSubmit = valid.includes(false);

  return (
    <>
      <Grid item xs={12} md={6}>
        <FormControl
          sx     = {{ display: 'flex', flexDirection: 'column' }}
          margin = 'normal'
        >
          <FormLabel required={required}>{t('first_name')}</FormLabel>
          <TextField
            hiddenLabel
            error    = {touched.first ? !valid[1] : false}
            onFocus  = {() => setTouched({...touched, first: true})}
            variant  = 'outlined'
            value    = {_user.first_name}
            onChange = {(e) =>
              _user.set('first_name', e.target.value, !dontSave)
            }
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl
          sx     = {{ display: 'flex', flexDirection: 'column' }}
          margin = 'normal'
        >
          <FormLabel required={required}>{t('last_name')}</FormLabel>
          <TextField
            hiddenLabel
            value    = {_user.last_name}
            error    = {touched.last ? !valid[2] : false}
            onFocus  = {() => setTouched({...touched, last: true})}
            variant  = 'outlined'
            onChange = {(e) =>
              _user.set('last_name', e.target.value, !dontSave)
            }
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl
          sx     = {{ display: 'flex', flexDirection: 'column' }}
          margin = 'normal'
        >
          <FormLabel>{t('title')}</FormLabel>
          <TextField
            hiddenLabel
            variant  = 'outlined'
            value    = {_user.title}
            onChange = {(e) => _user.set('title', e.target.value, !dontSave)}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl
          sx     = {{ display: 'flex', flexDirection: 'column' }}
          margin = 'normal'
        >
          <FormLabel>{t('phone_number')}</FormLabel>
          <TextField
            fullWidth
            hiddenLabel
            type     = 'phone'
            variant  = 'outlined'
            value    = {_user.phone || ''}
            onChange = {(e) => _user.set('phone', e.target.value, !dontSave)}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl
          sx     = {{ display: 'flex', flexDirection: 'column' }}
          margin = 'normal'
        >
          <FormLabel required={required}>{t('email')}</FormLabel>
          <TextField
            hiddenLabel
            type       = 'email'
            variant    = 'outlined'
            error      = {touched.mail ? !(valid[5] && valid[0]) : false}
            onFocus    = {() => setTouched({...touched, mail: true})}
            disabled   = {!dontSave}
            helperText = {!emailValidation(_user.email) && 'Invalid email'}
            value      = {_user.email}
            onChange   = {(e) => _user.set('email', e.target.value, !dontSave)}
          />
        </FormControl>
      </Grid>

      {hasPersonalNumber && (
        <Grid item xs={12} md={6}>
          <FormControl
            sx     = {{ display: 'flex', flexDirection: 'column' }}
            margin = 'normal'
          >
            <FormLabel required={required}>{t('personal_number')}</FormLabel>
            <TextField
              hiddenLabel
              type        = 'number'
              error       = {touched.pn ? !valid[3] : false}
              onFocus     = {() => setTouched({...touched, pn: true})}
              InputProps  = {{ inputProps: { minLength: 12, maxLength: 12 } }}
              placeholder = {t('yyyymmddxxxx')}
              variant     = 'outlined'
              value       = {_user.personal_number}
              onChange    = {(e) =>
                _user.set('personal_number', e.target.value, !dontSave)
              }
            />
          </FormControl>
        </Grid>
      )}

      {!dontSave && (
        <Grid item xs={12} md={6}>
          <FormControl
            sx     = {{ display: 'flex', flexDirection: 'column' }}
            margin = 'normal'
          >
            <FormLabel>{t('language')}</FormLabel>
            <Select
              value    = {authStore.getCurUser.language || Language.EN}
              onChange = {(e) =>
                authStore.getCurUser.changeLanguage(e.target.value as Language)
              }
            >
              {Object.values(Language).map((l) => (
                <MenuItem key={l} value={l}>
                  {t(l)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      
      {isSystemUser && (
        <Grid item xs={12} md={6}>
          <FormControl
            sx     = {{ display: 'flex', flexDirection: 'column' }}
            margin = 'normal'
          >
            <FormLabel required={required}>{t('user_role')}</FormLabel>
            <Select
              value    = {user.user_type}
              onChange = {(e) =>
                user.set('user_type', e.target.value as UserType, !dontSave)
              }
            >
              <MenuItem value={UserType.Admin}>{t('admin')}</MenuItem>
              <MenuItem value={UserType.Analyst}>{t('analyst')}</MenuItem>
              <MenuItem value={UserType.Editor}>{t('editor')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}

      {selectRole && (
        <Grid item xs={12} md={6}>
          <FormControl
            sx     = {{ display: 'flex', flexDirection: 'column' }}
            margin = 'normal'
          >
            <FormLabel required={required}>{t('user_role')}</FormLabel>
            <Select
              value    = {user.user_type}
              onChange = {(e) =>
                user.set('user_type', e.target.value as UserType, !dontSave)
              }
            >
              <MenuItem value={UserType.Participant}>{t('participant')}</MenuItem>
              <MenuItem value={UserType.Manager}>{t('manager')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}

      {(isSystemUser && user.user_type === UserType.Editor) && (
        <Grid item xs={12} md={6}>
          <FormControl
            sx     = {{ display: 'flex', flexDirection: 'column' }}
            margin = 'normal'
          >
            <FormLabel>{t('affiliation')}</FormLabel>
            <FormControlLabel
              label = {t('external')}
              labelPlacement = 'end'
              control = {
                <Switch
                  checked  = {user.external_editor || false}
                  onChange = {() =>
                    user.set(
                      'external_editor',
                      !user.external_editor,
                      !dontSave
                    )
                  }
                />
              }
            />
          </FormControl>
        </Grid>
      )}

      {hasConfirmationButtons && (
        <Grid item xs={12} md={12}>
          <FormControl
            sx     = {{ display: 'flex', flexDirection: 'row' }}
            margin = 'normal'
          >
            <Tooltip
              title = {disableSubmit ? t('tooltip_fill_required_fields') : ''}
            >
              <Box>
                <Button
                  variant  = 'contained'
                  disabled = {disableSubmit}
                  onClick  = {onSave}
                >
                  {t('save')}
                </Button>
              </Box>
            </Tooltip>
            <Button variant='text' onClick={onCancel}>
              {t('cancel')}
            </Button>
          </FormControl>
        </Grid>
      )}
    </>
  )
}));
